<template>
  <SectionPageShell section="packages" title="Zahlungsstatus" show-back-link>
    <section class="payment-return py-3 py-lg-4">
    <p class="text-muted mb-4">
      Der aktuelle Status Ihrer Paketbuchung wird automatisch abgerufen.
    </p>

    <div v-if="isLoading" class="alert alert-light border d-flex align-items-center gap-2">
      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      Zahlungsstatus wird geladen...
    </div>

    <div v-else-if="loadError" class="alert alert-danger border">
      {{ loadError }}
    </div>

    <article v-else class="payment-result-card">
      <div class="payment-result-card__eyebrow">Ergebnis</div>
      <h2 class="payment-result-card__title mb-2">{{ statusMeta.title }}</h2>
      <p class="mb-3">{{ statusDescription }}</p>

      <div v-if="isPolling" class="alert alert-light border d-flex align-items-center gap-2 mb-3">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        Zahlung wird weiter geprüft ({{ pollAttempt }}/{{ maxPollRetries }}).
      </div>

      <div v-if="showSupportHint" class="alert alert-warning border mb-3">
        Der Status konnte nicht final bestätigt werden. Bitte wenden Sie sich an den Support:
        <router-link :to="{name: 'account.support'}">/account/support</router-link>
      </div>

      <div v-if="normalizedStatus === 'paid'" class="payment-result-next alert alert-success border mb-3">
        <strong>Nächste Schritte:</strong>
        <ul class="mb-0 mt-2">
          <li>Neue Vermarktungsobjekte anlegen und aktivieren, damit Ihr Paket direkt genutzt wird.</li>
          <li>Bestehende Unterkünfte inhaltlich verbessern (Bilder, Texte, Datenqualität) für mehr Sichtbarkeit.</li>
          <li>Premium-Funktionen gezielt einsetzen, um Reichweite und Performance zu steigern.</li>
        </ul>
      </div>

      <div class="payment-result-card__details" v-if="statusLabel || paymentReference">
        <div v-if="statusLabel" class="payment-result-card__line">
          <span>Status</span>
          <strong>{{ statusLabel }}</strong>
        </div>
        <div v-if="paymentReference" class="payment-result-card__line">
          <span>Referenz</span>
          <strong>{{ paymentReference }}</strong>
        </div>
        <div v-if="transactionId" class="payment-result-card__line">
          <span>Transaktion</span>
          <strong>{{ transactionId }}</strong>
        </div>
      </div>

      <div class="d-flex flex-wrap gap-2 mt-4">
        <router-link :to="{name: 'packages.index'}" class="btn btn-warning">
          Paketübersicht öffnen
        </router-link>
        <router-link v-if="normalizedStatus === 'paid'" :to="{name: 'accommodations.index'}" class="btn btn-success">
          Einträge verwalten
        </router-link>
        <router-link v-if="canRetry" :to="{name: 'dashboard'}" class="btn btn-outline-secondary">
          Zum Dashboard
        </router-link>
      </div>
    </article>
    </section>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {usePackagesStore} from "@/stores/packages";
import {useUserStore} from "@/stores/user";

const POLL_INTERVAL_MS = 6000;
const MAX_POLL_RETRIES = 10;

const STATUS_META = {
  open: {
    title: 'Zahlung wurde gestartet',
    description: 'Die Zahlung ist angelegt, wurde aber noch nicht abgeschlossen.',
    poll: true,
    supportNow: false,
  },
  pending: {
    title: 'Zahlung wird verarbeitet',
    description: 'Die Zahlung ist gestartet und wird aktuell vom Zahlungsanbieter verarbeitet.',
    poll: true,
    supportNow: false,
  },
  authorized: {
    title: 'Zahlung autorisiert',
    description: 'Die Zahlung ist autorisiert. Die finale Bestätigung folgt nachgelagert.',
    poll: true,
    supportNow: false,
  },
  paid: {
    title: 'Zahlung erfolgreich abgeschlossen',
    description: 'Ihre Zahlung wurde erfolgreich bestätigt.',
    poll: false,
    supportNow: false,
  },
  failed: {
    title: 'Zahlung fehlgeschlagen',
    description: 'Die Zahlung konnte nicht abgeschlossen werden.',
    poll: false,
    supportNow: false,
  },
  canceled: {
    title: 'Zahlung abgebrochen',
    description: 'Die Zahlung wurde abgebrochen.',
    poll: false,
    supportNow: false,
  },
  expired: {
    title: 'Zahlung abgelaufen',
    description: 'Die Zahlungsfrist ist abgelaufen.',
    poll: false,
    supportNow: false,
  },
  chargeback: {
    title: 'Rückbuchung erkannt',
    description: 'Zu dieser Zahlung liegt eine Rückbuchung vor.',
    poll: false,
    supportNow: true,
  },
};

export default {
  name: 'PackagesPaymentReturn',
  components: {
    SectionPageShell,
  },
  setup() {
    return {
      packagesStore: usePackagesStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      isLoading: true,
      loadError: '',
      paymentStatus: null,
      paymentReference: '',
      transactionId: '',
      pollAttempt: 0,
      maxPollRetries: MAX_POLL_RETRIES,
      isPolling: false,
      showSupportHint: false,
      pollTimer: null,
    }
  },
  computed: {
    normalizedStatus() {
      return String(this.paymentStatus || '').toLowerCase().trim();
    },
    statusMeta() {
      return STATUS_META[this.normalizedStatus] || {
        title: 'Zahlungsstatus empfangen',
        description: 'Wir haben eine Rückmeldung vom Zahlungsanbieter erhalten.',
        poll: false,
        supportNow: true,
      };
    },
    statusLabel() {
      return this.paymentStatus || '-';
    },
    expectedTransactionId() {
      return String(this.$route.query.payment_transaction_id || '').trim();
    },
    isExpectedTransactionResolved() {
      if (!this.expectedTransactionId) {
        return this.normalizedStatus === 'paid';
      }

      return String(this.transactionId || '') === this.expectedTransactionId && this.normalizedStatus === 'paid';
    },
    statusDescription() {
      if (this.normalizedStatus === 'paid') {
        return 'Ihr Paket wurde erfolgreich bestellt. Die Paketdaten werden aktualisiert.';
      }
      return this.statusMeta.description;
    },
    canRetry() {
      return this.normalizedStatus !== 'paid';
    },
  },
  async created() {
    await this.fetchPaymentStatus({initial: true});
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      this.isPolling = false;
    },
    scheduleNextPoll() {
      this.stopPolling();
      this.isPolling = true;
      this.pollTimer = setTimeout(async () => {
        this.pollAttempt += 1;
        await this.fetchPaymentStatus({initial: false});
      }, POLL_INTERVAL_MS);
    },
    shouldContinuePolling() {
      if (this.statusMeta.supportNow) {
        return false;
      }
      if (this.isExpectedTransactionResolved) {
        return false;
      }
      if (this.expectedTransactionId && String(this.transactionId || '') !== this.expectedTransactionId) {
        return this.pollAttempt < this.maxPollRetries;
      }
      if (!this.statusMeta.poll) {
        return false;
      }
      return this.pollAttempt < this.maxPollRetries;
    },
    async fetchPaymentStatus({initial = false} = {}) {
      if (initial) {
        this.isLoading = true;
      }
      this.loadError = '';

      try {
        const kdnr = this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr;
        const paymentTransactionId = String(this.$route.query.payment_transaction_id || '').trim();
        const endpoint = paymentTransactionId ? `/payments/${paymentTransactionId}` : '/payments/current';
        const response = await this.packagesStore.apiClientV2.get(endpoint, {
          params: {
            ...(kdnr ? {kdnr} : {}),
          },
        });

        const payload = response?.data || {};
        const transaction = payload?.payment_transaction || {};
        const mollie = transaction?.mollie || {};

        this.paymentStatus = payload?.mollie_status || mollie?.status || payload?.status || payload?.payment_status || payload?.state || null;
        this.paymentReference = mollie?.id || payload?.reference || payload?.payment_id || payload?.id || '';
        this.transactionId = transaction?.id || '';
        this.showSupportHint = false;

        if (this.normalizedStatus === 'paid') {
          await this.packagesStore.index();
          await this.userStore.apiUserData();
        }

        if (this.shouldContinuePolling()) {
          this.scheduleNextPoll();
        } else {
          this.stopPolling();
          if (this.statusMeta.supportNow || (this.statusMeta.poll && this.pollAttempt >= this.maxPollRetries)) {
            this.showSupportHint = true;
          }
        }
      } catch (error) {
        this.stopPolling();
        this.loadError = error?.response?.data?.message || 'Der Zahlungsstatus konnte nicht geladen werden. Bitte versuchen Sie es erneut.';
      } finally {
        if (initial) {
          this.isLoading = false;
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.payment-result-card {
  padding: 1.5rem;
  border: 1px solid #d8e1ea;
  background: #ffffff;
}

.payment-result-card__eyebrow {
  margin-bottom: 0.4rem;
  color: #5d7183;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.payment-result-card__title {
  color: #10273d;
}

.payment-result-card__details {
  border-top: 1px solid #e7edf3;
  padding-top: 0.75rem;
}

.payment-result-card__line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.35rem 0;
}

.payment-result-card__line span {
  color: #5d7183;
}

.payment-result-card__line strong {
  color: #10273d;
}

.payment-result-next ul {
  padding-left: 1.15rem;
}
</style>
